import { Activity, Building2, CalendarRange, DatabaseBackup, FileBarChart, Globe, LayoutDashboard, LayoutGrid, Lock, MapPin, Palette, ScrollText, Settings, ShieldCheck, Trophy } from 'lucide-react';
import { lazyComponent } from '@/platform/lazyComponent';
import type { PlatformModule } from '@/platform/modules';
import { baseRoutes } from './routes';
import './slots';

const AuditPage = lazyComponent(() => import('./settings/audit/AuditPage').then((module) => module.AuditPage));
const BackupPage = lazyComponent(() => import('./settings/BackupPage').then((module) => module.BackupPage));
const LocationsPage = lazyComponent(() => import('./settings/locations/LocationsPage').then((module) => module.LocationsPage));
const BrandingPage = lazyComponent(() => import('./settings/OrganizationPages').then((module) => module.BrandingPage));
const OrganizationPage = lazyComponent(() => import('./settings/OrganizationPages').then((module) => module.OrganizationPage));
const RegionalPage = lazyComponent(() => import('./settings/OrganizationPages').then((module) => module.RegionalPage));
const ReportSettingsPage = lazyComponent(() => import('./settings/report-settings/ReportSettingsPage').then((module) => module.ReportSettingsPage));
const RolesPage = lazyComponent(() => import('./settings/roles/RolesPage').then((module) => module.RolesPage));
const SystemPage = lazyComponent(() => import('./settings/SystemPage').then((module) => module.SystemPage));
const UsersPage = lazyComponent(() => import('./settings/users/UsersPage').then((module) => module.UsersPage));

/**
 * Base: the foundation every other module stands on, always installed. Sign-in, the organization and its settings, users and
 * permissions, backups, the reports list, the Apps screen and the shell around all of it. It knows no other module: they add
 * themselves to what it provides (menu entries, settings pages, reports, the home page).
 */
export const baseModule: PlatformModule = {
  id: 'base',
  nav: [
    { id: 'dashboard', label: 'Dashboard', to: '/', icon: LayoutDashboard, end: true, section: 'main', order: 10 },
    // One clear place for the two related lists. A coach who may only read seasons sees only that child.
    {
      id: 'sports-seasons',
      label: 'Sports & Seasons',
      to: '/sports',
      icon: Trophy,
      permission: ['sport.manage', 'season.view', 'team.view', 'team_season.view'],
      section: 'main',
      order: 38,
      children: [
        { id: 'sports', label: 'Sports', to: '/sports', icon: Trophy, permission: 'sport.manage', section: 'main', order: 10 },
        { id: 'seasons', label: 'Seasons', to: '/seasons', icon: CalendarRange, permission: 'season.view', section: 'main', order: 20 },
      ],
    },
    { id: 'reports', label: 'Reports', to: '/reports', icon: FileBarChart, permission: 'report.generate', section: 'main', order: 50 },
    { id: 'apps', label: 'Apps', to: '/apps', icon: LayoutGrid, permission: 'app.manage', section: 'bottom', order: 80 },
    { id: 'settings', label: 'Settings', to: '/settings', icon: Settings, permission: ['settings.manage', 'backup.manage'], section: 'bottom', order: 90 },
  ],
  routes: baseRoutes,
  sportTabs: [
    { id: 'seasons', label: 'Seasons', path: 'seasons', order: 30 },
    { id: 'positions', label: 'Positions', path: 'positions', order: 40 },
  ],
  settingsSections: [
    { id: 'organization', label: 'Organization', path: 'organization', icon: Building2, permission: 'settings.manage', order: 10, live: true, component: OrganizationPage },
    { id: 'branding', label: 'Branding', path: 'branding', icon: Palette, permission: 'settings.manage', order: 20, live: true, component: BrandingPage },
    { id: 'locations', label: 'Locations', path: 'locations', icon: MapPin, permission: 'settings.manage', order: 30, live: true, component: LocationsPage },
    { id: 'users', label: 'Users', path: 'users', icon: ShieldCheck, permission: 'user.manage', order: 50, live: true, component: UsersPage },
    { id: 'roles', label: 'Roles & Permissions', path: 'roles', icon: Lock, permission: 'user.manage', order: 60, live: true, component: RolesPage },
    { id: 'reports', label: 'Reports', path: 'reports', icon: FileBarChart, permission: 'settings.manage', order: 70, live: true, component: ReportSettingsPage },
    { id: 'backup', label: 'Backup & Restore', path: 'backup', icon: DatabaseBackup, permission: 'backup.manage', order: 80, live: true, component: BackupPage },
    { id: 'regional', label: 'Regional Settings', path: 'regional', icon: Globe, permission: 'settings.manage', order: 90, live: true, component: RegionalPage },
    { id: 'system', label: 'System & Diagnostics', path: 'system', icon: Activity, permission: 'settings.manage', order: 100, live: true, component: SystemPage },
    { id: 'audit', label: 'Audit log', path: 'audit', icon: ScrollText, permission: 'settings.manage', order: 110, live: true, component: AuditPage },
  ],
  access: {
    title: 'Administration',
    order: 10,
    levels: [
      { id: 'viewer', label: 'Viewer', description: 'See the seasons of each sport.', perms: ['season.view'] },
      { id: 'reports', label: 'Reports', description: 'Also create general reports.', perms: ['report.generate'] },
      { id: 'settings', label: 'Settings', description: 'Also change the organization\'s settings.', perms: ['settings.manage'] },
      { id: 'administrator', label: 'Administrator', description: 'Also add people, change what they may use, and install or remove apps.', perms: ['user.manage', 'app.manage'] },
    ],
    extras: [
      { id: 'sports', label: 'Sports', description: 'Add and edit sports and the positions each has.', perms: ['sport.manage'] },
      { id: 'seasons', label: 'Seasons', description: 'Add and change the seasons of each sport, complete and archive them, and delete one nobody uses.', perms: ['season.create', 'season.edit', 'season.archive'] },
      { id: 'backup', label: 'Backup and restore', description: 'Create backups and restore from one.', perms: ['backup.manage'] },
    ],
    permissions: {
      'report.generate': 'Create general reports',
      'settings.manage': 'Change organization settings',
      'user.manage': 'Add people and change what they may use',
      'app.manage': 'Install and uninstall apps',
      'sport.manage': 'Add and edit sports and their positions',
      'season.view': 'See the seasons of each sport',
      'season.create': 'Add a season to a sport',
      'season.edit': 'Change a season, its dates and its status',
      'season.archive': 'Archive and restore seasons, and delete one nobody uses',
      'backup.manage': 'Create and restore backups',
    },
  },
};
